import { useMemo } from "react";
import { object, string, number, mixed, array } from "yup";
import { REGEXP } from "consts/validation";
import { PROOF_FILE } from "consts/ecommerce";
import { adult, fileSize, fileType } from "util/validators";
import { FormField } from "./types";

const useValidationSchema = () =>
    useMemo(() => (
        object().shape({
            [FormField.CeoPassportFileKey]: mixed()
                .required()
                .test('fileSize', 'File size is too large', fileSize(PROOF_FILE.maxSize))
                .test('fileType', 'Unsupported file format', fileType(PROOF_FILE.supportedFormats))
                .label('Identity Document'),

            [FormField.CeoUtilityBillFileKey]: mixed()
                .required()
                .test('fileSize', 'File size is too large', fileSize(PROOF_FILE.maxSize))
                .test('fileType', 'Unsupported file format', fileType(PROOF_FILE.supportedFormats))
                .label('Proof of Address'),

            [FormField.CeoName]: string()
                .nullable()
                .required()
                .trim()
                .min(2)
                .max(100)
                .label('Full Name'),

            [FormField.CeoDateOfBirth]: string()
                .required()
                .nullable()
                .label('Date of Birth')
                .test(
                    'is-adult',
                    'You must be an adult to proceed',
                    adult()
                ),

            [FormField.CeoCountryOrigin]: array()
                .nullable()
                .required('Select at least one country')
                .of(string())
                .min(1, 'Select at least one country')
                .label('Nationality'),

            [FormField.CeoAddressLine1]: string()
                .nullable()
                .required()
                .trim()
                .min(5)
                .max(200)
                .label('Residential Address'),

            [FormField.CeoEmail]: string()
                .nullable()
                .required()
                .trim()
                .email('Enter a valid email address')
                .max(100)
                .label('E-mail'),

            [FormField.CeoPhone]: string()
                .nullable()
                .required()
                .trim()
                .matches(REGEXP.Phone, 'Enter a valid phone number')
                .max(20)
                .label('Phone'),

            [FormField.IsPepCeo]: number()
                .nullable()
                .required(),

            [FormField.AuthSigPassportFileKey]: mixed()
                .test('fileSize', 'File size is too large', fileSize(PROOF_FILE.maxSize))
                .test('fileType', 'Unsupported file format', fileType(PROOF_FILE.supportedFormats))
                .label('Identity Document'),

            [FormField.AuthSigUtilityBillFileKey]: mixed()
                .test('fileSize', 'File size is too large', fileSize(PROOF_FILE.maxSize))
                .test('fileType', 'Unsupported file format', fileType(PROOF_FILE.supportedFormats))
                .label('Proof of Address'),

            [FormField.AuthSigName]: string()
                .nullable()
                .trim()
                .min(2)
                .max(100)
                .label('Full Name'),

            [FormField.AuthSigDateOfBirth]: string()
                .nullable()
                .label('Date of Birth')
                .test(
                    'is-adult',
                    'You must be an adult to proceed',
                    adult(false)
                ),

            [FormField.AuthSigCountryOrigin]: array()
                .nullable()
                .of(string())
                .min(1, 'Select at least one country')
                .label('Nationality'),

            [FormField.AuthSigAddressLine1]: string()
                .nullable()
                .trim()
                .min(5)
                .max(200)
                .label('Residential Address'),

            [FormField.AuthSigEmail]: string()
                .nullable()
                .trim()
                .email('Enter a valid email address')
                .max(100)
                .label('E-mail'),

            [FormField.AuthSigPhone]: string()
                .nullable()
                .trim()
                .matches(REGEXP.Phone, 'Enter a valid phone number')
                .max(20)
                .label('Phone'),

            [FormField.IsPepAuthSig]: number()
                .nullable(),

            [FormField.OtherPeople.FieldPrefix]: array()
                .optional()
                .of(
                    object().shape({
                        [FormField.OtherPeople.PassportFileKey]: mixed()
                            .required()
                            .test('fileSize', 'File size is too large', fileSize(PROOF_FILE.maxSize))
                            .test('fileType', 'Unsupported file format', fileType(PROOF_FILE.supportedFormats))
                            .label('Identity Document'),

                        [FormField.OtherPeople.UtilityBillFileKey]: mixed()
                            .required()
                            .test('fileSize', 'File size is too large', fileSize(PROOF_FILE.maxSize))
                            .test('fileType', 'Unsupported file format', fileType(PROOF_FILE.supportedFormats))
                            .label('Proof of Address'),

                        [FormField.OtherPeople.Name]: string()
                            .nullable()
                            .required()
                            .trim()
                            .min(2)
                            .max(100)
                            .label('Full Name'),

                        [FormField.OtherPeople.DateOfBirth]: string()
                            .required()
                            .nullable()
                            .label('Date of Birth')
                            .test(
                                'is-adult',
                                'You must be an adult to proceed',
                                adult()
                            ),

                        [FormField.OtherPeople.CountryOrigin]: array()
                            .nullable()
                            .required('Select at least one country')
                            .of(string())
                            .min(1, 'Select at least one country')
                            .label('Nationality'),

                        [FormField.OtherPeople.AddressLine1]: string()
                            .nullable()
                            .required()
                            .trim()
                            .min(5)
                            .max(200)
                            .label('Residential Address'),

                        [FormField.OtherPeople.Email]: string()
                            .nullable()
                            .required()
                            .trim()
                            .email('Enter a valid email address')
                            .max(100)
                            .label('E-mail'),

                        [FormField.OtherPeople.Phone]: string()
                            .nullable()
                            .required()
                            .trim()
                            .matches(REGEXP.Phone, 'Enter a valid phone number')
                            .max(20)
                            .label('Phone'),

                        [FormField.OtherPeople.IsPep]: number()
                            .nullable()
                            .required(),

                        [FormField.OtherPeople.SharesPercentage]: number()
                            .nullable()
                            .required()
                            .min(0, 'Must be at least 0%')
                            .max(100, 'Cannot exceed 100%')
                            .label('Share Percentage')
                    })
                )
        })
    ), []);

export default useValidationSchema;
